import React, { useCallback, useEffect, useState } from 'react';
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useHistory, useLocation } from 'react-router';
import { connect } from 'react-redux';
import { object, string } from 'yup';
import { Form, Formik } from 'formik';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';

import toast from 'next/lib/toast';
import { Shape as UserShape } from 'next/entities/user';
import { calculatePlanPriceFromLocation } from 'utils/billing';
import { createPlanId } from 'utils/stripe';
import { selectAccountMeta } from 'reducers/account/meta';
import { asPromised } from 'utils/as-promised';
import { createSubscription, setupPaymentIntent } from 'actions/newBilling';
import {
  selectClientSecret,
  selectPaymentTaxes,
  selectSubscription,
} from 'reducers/newBilling';
import { selectUser } from 'reducers/user';
import BillingAddressForm from './BillingAddressForm';
import PaymentMethodForm from './PaymentMethodForm';
import CheckoutSummary from './CheckoutSummary';
import { CheckoutLayout, FormWrapper } from './styled';

const FORM_INITIAL_VALUES = {
  city: '',
  country: '',
  line1: '',
  name: '',
  postalCode: '',
};

const FORM_SCHEMA = object().shape({
  city: string().required('City is required'),
  country: object().required('Please select a country from the list'),
  line1: string().required('Billing address is required'),
  name: string().required('Name on card is required'),
  postalCode: string()
    .required('Postal code is required')
    .test('Empty', 'Postal code is required', val => !!val)
    .when('country', {
      is: val => val?.value === 'US',
      then: string()
        .test('is-numeric', 'Please enter a valid postal code', val =>
          /^\d+$/.test(val)
        )
        .min('5', 'Postal code must contain 5 digits')
        .max('5', 'Postal code must contain 5 digits'),
      otherwise: string()
        .test('is-alphanumeric', 'Please enter a valid postal code', val =>
          /^[\d\sA-Za-z]+$/.test(val)
        )
        .max('11', 'Postal code must be less than 12 characters'),
    }),
});

export const CheckoutForm = ({
  account,
  clientSecret,
  onSetupPaymentIntent,
  onCreateSubscription,
  subscriptionTaxes,
  subscription,
  user,
}) => {
  const stripe = useStripe();
  const history = useHistory();
  const location = useLocation();
  const elements = useElements();

  const [stripeId, setStripeId] = useState({});
  const [planData, setPlanData] = useState({});
  const [subscriptionLoading, setSubscriptionLoading] = useState(null);
  const [completedForms, setCompletedForms] = useState({
    paymentMethod: false,
    billing: false,
  });

  // This hook calculates the plan's data with the URL query params
  useEffect(() => {
    if (location?.search && isEmpty(planData)) {
      setPlanData(calculatePlanPriceFromLocation(location));
    }
  }, [location, planData, setPlanData]);

  // This hook sets the stripe ID based on the URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeIdFromParams = params.get('st');
    if (stripeIdFromParams) setStripeId(stripeIdFromParams);
  }, [location, setStripeId]);

  // Once we have the stripe ID, setup the payment intent and load the Stripe elements
  useEffect(() => {
    if (!clientSecret && stripeId) onSetupPaymentIntent({ stripeId });
  }, [clientSecret, stripeId, onSetupPaymentIntent]);

  // Once we have a subscription in the store, redirect to the subscription page
  useEffect(() => {
    if (subscription && subscriptionLoading === true) {
      history.push(`/settings/subscription?id=${subscription.id}`);
    }
  }, [history, subscription, subscriptionLoading]);

  const handleFormSubmit = useCallback(async formValues => {
    const card = elements.getElement(CardNumberElement);

    setSubscriptionLoading(true);

    const planId = createPlanId(
      planData.price,
      planData.id,
      planData.planInterval
    );

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        card,
        billing_details: {
          name: formValues.name,
          email: user.email,
        },
        type: 'card',
      });

      if (error) {
        throw error;
      }

      onCreateSubscription({
        accountId: account.id,
        billingAddress: {
          line1: formValues.line1,
          country: formValues.country.value,
          postalCode: formValues.postalCode,
          city: formValues.city,
        },
        email: user.email,
        paymentMethodId: paymentMethod.id,
        planId,
        stripeId,
        taxes: subscriptionTaxes,
        userId: user.id,
        mau: planData.planLimit,
      })
        .then(() => setSubscriptionLoading(false))
        .catch(() => setSubscriptionLoading(false));
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
      setSubscriptionLoading(false);
    }
  });

  if (!clientSecret) return null;

  return (
    <Formik
      initialValues={FORM_INITIAL_VALUES}
      validationSchema={FORM_SCHEMA}
      onSubmit={handleFormSubmit}
    >
      {({ errors, values, dirty, handleSubmit }) => {
        return (
          <Form
            onSubmit={e => {
              e.preventDefault();
              handleSubmit(e);
            }}
          >
            <CheckoutLayout>
              <FormWrapper>
                <BillingAddressForm
                  errors={errors}
                  formValues={values}
                  onCompleteForm={newFormCompleted => {
                    setCompletedForms({
                      ...completedForms,
                      ...newFormCompleted,
                    });
                  }}
                />
                <PaymentMethodForm
                  dirty={dirty}
                  formValues={values}
                  onCompleteForm={newFormCompleted => {
                    setCompletedForms({
                      ...completedForms,
                      ...newFormCompleted,
                    });
                  }}
                />
              </FormWrapper>
              <CheckoutSummary
                completedForms={completedForms}
                formValues={values}
                isSubmitting={subscriptionLoading}
                planData={planData}
              />
            </CheckoutLayout>
          </Form>
        );
      }}
    </Formik>
  );
};

CheckoutForm.propTypes = {
  account: PropTypes.object,
  clientSecret: PropTypes.string,
  onSetupPaymentIntent: PropTypes.func,
  onCreateSubscription: PropTypes.func,
  subscriptionTaxes: PropTypes.number,
  subscription: PropTypes.shape({
    id: PropTypes.string,
  }),
  user: UserShape,
};

const mapStateToProps = state => ({
  account: selectAccountMeta(state),
  clientSecret: selectClientSecret(state),
  subscriptionTaxes: selectPaymentTaxes(state),
  subscription: selectSubscription(state),
  user: selectUser(state),
});

const mapDispatchToProps = dispatch => ({
  onCreateSubscription: payload =>
    asPromised(dispatch, createSubscription(payload)),
  onSetupPaymentIntent: payload =>
    asPromised(dispatch, setupPaymentIntent(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutForm);
